import { Method } from 'axios';
import { subYears } from 'date-fns';
import { Spaces } from 'lib/bytes';
import { extInfo } from 'lib/extInfo';
import { APICallV4 } from 'reactApp/api/APICall';
import { letterGroups } from 'reactApp/modules/userQuotaCleaner/helpers/constants';
import {
    Folders,
    UserQuotaCleanerDelete,
    UserQuotaCleanerGroup,
    UserQuotaCleanerListParams,
    UserQuotaGroupId,
    UserQuotaGroupType,
} from 'reactApp/types/QuotaCleaner';

const getTimeYearShifted = (date: Date, yearsBefore: number): number => Math.floor(subYears(date, yearsBefore).getTime() / 1000);
const getGroupType = (groupId: UserQuotaGroupId) => (letterGroups.includes(groupId) ? UserQuotaGroupType.Letter : UserQuotaGroupType.Cloud);

// Список актуальных метатредов лежит в репе JSSDK
const getUserMetathreads = (folders) =>
    [Folders.News, Folders.NewsLetters, Folders.Social, Folders.ToMySelf].filter((folder) => folders.includes(folder));

export const prepareParamsQuotaCleaner = (
    folders: string[],
    userFolders: string[],
    limit: number,
    excludedGroupIds: UserQuotaGroupId[] = []
): Partial<Record<UserQuotaGroupId, UserQuotaCleanerListParams>> => {
    const now = new Date();
    const metathreads = getUserMetathreads(folders);

    const config = {
        [UserQuotaGroupId.Mounted]: {
            group_type: getGroupType(UserQuotaGroupId.Mounted),
            group_id: UserQuotaGroupId.Mounted,
            limit,
            mounted: true,
        },
        [UserQuotaGroupId.Backup]: {
            group_type: getGroupType(UserQuotaGroupId.Backup),
            group_id: UserQuotaGroupId.Backup,
            limit,
            folders: [Folders.Backups],
        },
        [UserQuotaGroupId.CloudLarge]: {
            group_type: getGroupType(UserQuotaGroupId.CloudLarge),
            group_id: UserQuotaGroupId.CloudLarge,
            limit,
            min_size: 100 * Spaces.ONE_MB,
        },
        [UserQuotaGroupId.Video]: {
            group_type: getGroupType(UserQuotaGroupId.Video),
            group_id: UserQuotaGroupId.Video,
            limit,
            extensions: extInfo.getExtsByKind('video'),
        },
        [UserQuotaGroupId.CloudOld]: {
            group_type: getGroupType(UserQuotaGroupId.CloudOld),
            group_id: UserQuotaGroupId.CloudOld,
            limit,
            older_than: getTimeYearShifted(now, 2),
        },
        [UserQuotaGroupId.CameraUploads]: {
            group_type: getGroupType(UserQuotaGroupId.CameraUploads),
            group_id: UserQuotaGroupId.CameraUploads,
            limit,
            folders: [Folders.CameraUploads],
        },
        [UserQuotaGroupId.MailBin]: {
            group_type: getGroupType(UserQuotaGroupId.MailBin),
            group_id: UserQuotaGroupId.MailBin,
            limit,
            folders: [Folders.LetterTrashbin],
        },
        [UserQuotaGroupId.MailSpam]: {
            group_type: getGroupType(UserQuotaGroupId.MailSpam),
            group_id: UserQuotaGroupId.MailSpam,
            limit,
            folders: [Folders.LetterSpam],
        },
        [UserQuotaGroupId.Newsletters]: {
            group_type: getGroupType(UserQuotaGroupId.Newsletters),
            group_id: UserQuotaGroupId.Newsletters,
            limit,
            newsletter: true,
            folders: [Folders.LetterInbox, Folders.NewsLetters, ...metathreads, ...userFolders],
        },
        [UserQuotaGroupId.LargeSent]: {
            group_type: getGroupType(UserQuotaGroupId.LargeSent),
            group_id: UserQuotaGroupId.LargeSent,
            limit,
            send_by: true,
            min_size: 25 * Spaces.ONE_MB,
            folders: [Folders.LetterSent, ...metathreads, ...userFolders],
        },
        [UserQuotaGroupId.LargeInbox]: {
            group_type: getGroupType(UserQuotaGroupId.LargeInbox),
            group_id: UserQuotaGroupId.LargeInbox,
            limit,
            send_by: false,
            min_size: 25 * Spaces.ONE_MB,
            folders: [Folders.LetterInbox, ...metathreads, ...userFolders],
        },
        [UserQuotaGroupId.OldSent]: {
            group_type: getGroupType(UserQuotaGroupId.OldSent),
            group_id: UserQuotaGroupId.OldSent,
            limit,
            send_by: true,
            older_than: getTimeYearShifted(now, 2),
            folders: [Folders.LetterSent, ...metathreads, ...userFolders],
        },
        [UserQuotaGroupId.Read]: {
            group_type: getGroupType(UserQuotaGroupId.Read),
            group_id: UserQuotaGroupId.Read,
            limit,
            mark_read: true,
            mark_flagged: false,
            folders: [Folders.LetterInbox, ...metathreads, ...userFolders],
        },
        [UserQuotaGroupId.WithAttaches]: {
            group_type: getGroupType(UserQuotaGroupId.WithAttaches),
            group_id: UserQuotaGroupId.WithAttaches,
            limit,
            with_attach: true,
            older_than: getTimeYearShifted(now, 1),
            folders: [...folders.filter((folder) => folder !== Folders.LetterTrashbin && folder !== Folders.Official)],
        },
        [UserQuotaGroupId.OldInbox]: {
            group_type: getGroupType(UserQuotaGroupId.OldInbox),
            group_id: UserQuotaGroupId.OldInbox,
            limit,
            older_than: getTimeYearShifted(now, 1),
            folders: [Folders.LetterInbox, ...metathreads, ...userFolders],
        },
    };

    return Object.keys(config).reduce((acc, groupId) => {
        if (!excludedGroupIds.includes(groupId as UserQuotaGroupId)) {
            acc[groupId] = config[groupId];
        }

        return acc;
    }, {});
};

export class QuotaCleanerListAPICall extends APICallV4<{ body?: UserQuotaCleanerGroup[] }> {
    _method = 'user/quota/list';
    _type: Method = 'post';
}

export class QuotaCleanerGroupAPICall extends APICallV4<{ body?: UserQuotaCleanerGroup }> {
    _method = 'user/quota/group';
    _type: Method = 'post';
}

export class QuotaCleanerDeleteAPICall extends APICallV4<{ body?: UserQuotaCleanerDelete }> {
    _method = 'user/quota/delete';
    _type: Method = 'post';
}
